import { BookOutlined, RedoOutlined } from '@ant-design/icons'
import { Card, Checkbox, Col, Input, Row, Tooltip, Typography } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import FormEditWord from 'src/common/DictionaryModal/components/Dictionary/components/FormEditWord/FormEditWord'
import {
	onGetDefinition,
	onResetDefinition,
} from 'src/redux/definition/definition.actions'
import { useDebounce } from 'use-debounce'
import {
	ButtonCommon,
	DictionaryModal,
	ListPageIntro,
	ListPageShell,
	ListPageTotal,
	PaginationCommon,
	PopoverCommon,
	TableCommon,
} from '../../common'
import { useAppDispatch, useAppSelector } from '../../hooks'
import {
	onGetListDictWord,
	onSelectDictWord,
} from '../../redux/dict/dict.action'
import {
	selectCurrentPageDictWord,
	selectListDictWords,
	selectTotalDictWord,
} from '../../redux/dict/dict.selector'
import { DEFAULT_PAGE_SIZE } from '../../utils/constant'
import { formatDictWordMeaningsLines } from '../../utils/dictWordListFormat'
import { DictWordsAction } from './components/DictWordsAction/DictWordsAction'
import './DictList.scss'

export const DictList = () => {
	const location = useLocation()
	const [modalVisible, setModalVisible] = useState(false)
	const dispatch = useAppDispatch()
	const data = useAppSelector(selectListDictWords)
	const total = useAppSelector(selectTotalDictWord)
	const currentPage = useAppSelector(selectCurrentPageDictWord)

	const [textSearch, setTextSearch] = useState(
		() => new URLSearchParams(location.search).get('word') || ''
	)
	const [keyword] = useDebounce(textSearch, 200)
	const [isExactWord, setIsExactWord] = useState(false)
	const checkboxRef = useRef()
	const [isLoading, setIsLoading] = useState()

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 72,
		},
		{
			title: 'Word',
			dataIndex: 'word',
			key: 'word',
		},
		{
			title: 'Spelling',
			dataIndex: 'spelling',
			key: 'spelling',
		},
		{
			title: 'SpellingUk',
			dataIndex: 'spellingUk',
			key: 'spellingUk',
		},
		{
			title: 'Meanings',
			key: 'meanings',
			width: 360,
			render: (_, record) => {
				const lines = formatDictWordMeaningsLines(record)
				if (!lines.length) {
					return (
						<Typography.Text
							type="secondary"
							className="dict-list__meanings-empty"
						>
							—
						</Typography.Text>
					)
				}
				const tooltipTitle = lines.join('\n')
				const inline = lines.join(' · ')
				return (
					<Tooltip
						title={tooltipTitle}
						placement="left"
						overlayClassName="dict-list__meanings-tooltip"
					>
						<Typography.Paragraph
							className="dict-list__meanings-preview"
							ellipsis={{ rows: 3, expandable: false }}
						>
							{inline}
						</Typography.Paragraph>
					</Tooltip>
				)
			},
		},
		{
			title: '',
			dataIndex: 'id',
			key: 'action',
			width: 120,
			render: (_, record) => {
				return <DictWordsAction data={record} onEdit={onEditClick} />
			},
		},
	]
	const onGetDataCb = useCallback(() => {
		setIsLoading(false)
	}, [])
	const onCheckIsExactWord = e => {
		e.preventDefault()
		setIsExactWord(e.target.checked)
	}

	const onTextSearchChanged = e => {
		setTextSearch(e.target.value)
	}

	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const w = params.get('word')
		if (w != null && w !== '') {
			setTextSearch(w)
		}
	}, [location.search])

	const onChangePage = page => {
		setIsLoading(true)
		dispatch(
			onGetListDictWord(
				{
					pageNum: page - 1,
					pageSize: DEFAULT_PAGE_SIZE,
					keyword,
					isExact: isExactWord,
					includeDefinitions: true,
				},
				onGetDataCb
			)
		)
	}

	useEffect(() => {
		setIsLoading(true)
		dispatch(
			onGetListDictWord(
				{
					pageNum: 0,
					pageSize: DEFAULT_PAGE_SIZE,
					keyword,
					isExact: isExactWord,
					includeDefinitions: true,
				},
				onGetDataCb
			)
		)
	}, [keyword, dispatch, onGetDataCb, isExactWord])

	const handleReload = () => {
		setIsLoading(true)
		dispatch(
			onGetListDictWord(
				{
					pageNum: 0,
					pageSize: DEFAULT_PAGE_SIZE,
					keyword,
					isExact: isExactWord,
					includeDefinitions: true,
				},
				onGetDataCb
			)
		)
	}

	const getRowKey = record => {
		return record.id
	}
	const handleModalCancel = () => {
		setModalVisible(false)
	}

	const onEditClick = record => {
		dispatch(onSelectDictWord(record))
		dispatch(
			onGetDefinition({ word: record.word, isForce: true }, () => {})
		)

		setModalVisible(true)
	}

	const onAddClick = () => {
		dispatch(onResetDefinition(''))
	}

	return (
		<ListPageShell>
			<div className="list-page-bar">
				<ListPageIntro
					icon={BookOutlined}
					title="Dictionary words"
					subtitle="Search and manage dictionary entries. Enable exact match to find a full word only."
				/>
				<ListPageTotal total={total} />
			</div>
			<Card className="list-page-toolbar-card dict-list__toolbar" size="small" bordered>
				<Row gutter={[16, 16]} align="middle" justify="space-between">
					<Col xs={24} lg={14}>
						<div className="dict-list__search-row">
							<Input
								placeholder="Search word"
								value={textSearch}
								onChange={onTextSearchChanged}
								allowClear
								className="dict-list__search-input"
							/>
							<Checkbox onChange={onCheckIsExactWord} ref={checkboxRef}>
								Exact word
							</Checkbox>
						</div>
					</Col>
					<Col xs={24} lg={10}>
						<div className="dict-list__toolbar-actions">
							<ButtonCommon
								onClick={handleReload}
								icon={<RedoOutlined />}
								type="primary"
							>
								Reload
							</ButtonCommon>
							<PopoverCommon content={<FormEditWord />} trigger="click">
								<ButtonCommon
									size="small"
									ghost
									type="primary"
									onClick={onAddClick}
								>
									+ Add word
								</ButtonCommon>
							</PopoverCommon>
						</div>
					</Col>
				</Row>
			</Card>

			<TableCommon
				className="table-common--fluid"
				columns={columns}
				dataSource={data}
				pagination={false}
				rowKey={getRowKey}
				loading={isLoading}
			/>
			<PaginationCommon
				align="center"
				total={total}
				currentPage={currentPage}
				onChangePage={onChangePage}
			/>
			<DictionaryModal visible={modalVisible} onClose={handleModalCancel} />
		</ListPageShell>
	)
}
